/**
 * The verification questionnaire — Keepz's "KYC application for legal entity" form.
 * A business registered to a company fills in all of it; one registered to an
 * individual fills in the highlighted part.
 *
 * Ids match mintly-back/src/constants/kyc.js; their words live in translations under
 * `kyc.*`.
 */

/**
 * Whether a new business has to fill in the verification form. Off for now: creating a
 * business is just the business, and its page shows no verification badge. Set to true —
 * with its twin in mintly-back/src/constants/kyc.js — to ask for it again.
 */
export const BUSINESS_VERIFICATION_ENABLED = false;

export const KYC_STATUSES = ["pending", "approved", "declined"];

/** Who a business is registered to: a private person, or a company (LLC, JSC, IE…). */
export const APPLICANT_TYPES = ["individual", "company"];

export const LEGAL_TYPES = ["ie", "llc", "jsc", "other"];

/** Estimated annual turnover through the account, in GEL — figures read the same in any language. */
export const ANNUAL_TURNOVER_RANGES = [
  { id: "under-50k", label: "< 50,000 ₾" },
  { id: "50k-200k", label: "50,000 – 200,000 ₾" },
  { id: "200k-1m", label: "200,000 – 1,000,000 ₾" },
  { id: "over-1m", label: "> 1,000,000 ₾" },
];

/** "Please select the type of business" — in the form's order. */
export const BUSINESS_ACTIVITIES = [
  "retail-trade",
  "e-commerce",
  "marketplace",
  "it-software",
  "digital-services",
  "educational-services",
  "tourism-services",
  "ticket-sales",
  "subscription-services",
  "gaming",
  "gambling",
  "financial-services",
  "forex",
  "crypto",
  "investment",
  "consulting",
  "marketing",
  "logistics",
  "charity",
  "real-estate",
  "construction",
  "hospitality",
  "healthcare",
  "precious-goods",
  "other",
];

/** A beneficial owner holds at least this share, so a company can't have more than four. */
export const BENEFICIAL_OWNER_MIN_SHARE = 25;
export const MAX_BENEFICIAL_OWNERS = 4;

/** A director, or with `withShare` a beneficial owner. `pep: null` means not answered yet. */
export const emptyPerson = (withShare = false) => ({
  fullName: "",
  personalId: "",
  citizenship: "",
  pep: null,
  email: "",
  phone: "",
  ...(withShare ? { sharePercent: "" } : {}),
});

export const emptyKyb = () => ({
  companyName: "",
  brandName: "",
  legalType: "",
  legalTypeOther: "",
  identificationCode: "",
  website: "",
  iban: "",
  acceptsForeignCards: null,
  annualTurnover: "",
  businessActivities: [],
  businessActivityOther: "",
  director: emptyPerson(),
  beneficialOwners: [emptyPerson(true)],
  confirmed: false,
});

export const emptyKyc = () => ({
  fullName: "",
  personalId: "",
  businessActivities: [],
  businessActivityOther: "",
  email: "",
  phone: "",
});

/**
 * A business's form with both kinds' answers side by side, so switching between
 * individual and company loses nothing. Only the chosen kind's are checked; the
 * business activity is shared. Nothing is chosen to begin with.
 */
export const emptyVerification = () => ({
  applicantType: "",
  ...emptyKyb(),
  ...emptyKyc(),
});
